import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
  Activity,
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  CircleDot,
  Clock3,
  History,
  PauseCircle,
  Play,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  X,
} from "lucide-react"
import { useHotkeys } from "react-hotkeys-hook"
import toast from "react-hot-toast"
import {
  Badge,
  Button,
  ButtonWithConfirm,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@aix/ui"

import { trpc } from "../../lib/trpc"
import { AgentChatPanel } from "./AgentChatPanel"
import { RunLogDrawer } from "./RunLogDrawer"

interface Agent {
  id: number
  name: string
  description?: string | null
  repo_path: string
  base_branch: string
  runner: string
  model?: string | null
  system_prompt?: string | null
  skills: string[]
  setup_commands: string[]
  slack_channel_id?: string | null
  enabled: number
  created_by: string
  created_at: string
  updated_at: string
  triggers?: Trigger[]
}

interface Trigger {
  id: number
  agent_id: number
  type: "cron" | "message" | "manual"
  cron_expr?: string | null
  message_pattern?: string | null
  prompt: string
  enabled: number
  silent: number
  run_count: number
  last_run_at?: string | null
  last_error?: string | null
}

interface Run {
  id: number
  agent_id?: number
  status: "queued" | "running" | "completed" | "failed"
  prompt?: string | null
  branch?: string | null
  pr_url?: string | null
  error?: string | null
  triggered_by?: string | null
  started_at: string
  ended_at?: string | null
}

interface AgentDetailData {
  agent: Agent
  triggers?: Trigger[]
  recent_runs?: Run[]
}

interface CreateForm {
  name: string
  repo_path: string
  base_branch: string
  runner: string
  model: string
  description: string
  system_prompt: string
  skills: string
  setup_commands: string
  slack_channel_id: string
}

interface TriggerForm {
  type: "cron" | "message" | "manual"
  prompt: string
  cron_expr: string
  message_pattern: string
}

interface OwnershipForm {
  owner: string
  cadence: string
  escalation: string
  notes: string
}

interface AgentTriggerFire {
  id: number
  agent_id: number
  trigger_id: number
  cron_expr: string
  scheduled_for_pst: string
  dedup_key: string
  status: "missed" | "fired" | "failed"
  run_id?: number | null
  executor_job_id?: string | null
  error?: string | null
  source: string
  created_at: string
  fired_at?: string | null
  updated_at: string
}

interface CronTrustRow {
  agent: Agent | null
  trigger: Trigger
  enabled: boolean
  next_due_pst: string | null
  latest_fire: AgentTriggerFire | null
  latest_run: Run | null
  missed_count: number
  failed_count: number
  recent_fires: AgentTriggerFire[]
}

type DetailTab = "overview" | "chat" | "config" | "triggers" | "cron-trust" | "runs" | "ownership"
type StatusFilter = "all" | "healthy" | "running" | "failing" | "stale" | "disabled" | "no-recent-runs"
type CadenceFilter = "all" | "scheduled" | "message" | "manual" | "disabled"
type AgentHealth = "healthy" | "running" | "failing" | "stale" | "disabled" | "no-recent-runs"

interface AgentsSearch {
  agent?: number | string
  tab?: DetailTab
  q?: string
  status?: StatusFilter
  runner?: string
  cadence?: CadenceFilter
  owner?: string
  new?: string
  run?: number | string
  template?: string
}

interface AgentOpsRow {
  agent: Agent
  latestRun?: Run
  health: AgentHealth
  cadence: CadenceFilter
  owner: string
}

const RUNNING_STATUSES = new Set<Run["status"]>(["queued", "running"])
const STALE_RUN_DAYS = 3
const NO_RECENT_RUN_DAYS = 14

const STARTER_TEMPLATES: { label: string; description: string; preset: Partial<CreateForm> }[] = [
  {
    label: "Sentry triage",
    description: "Reads new Sentry errors hourly and opens a triage PR with proposed fixes.",
    preset: {
      name: "sentry-triage",
      description: "Reviews fresh Sentry issues and proposes fixes.",
      skills: "api-sentry, api-github-actions",
      system_prompt: "You triage incoming Sentry errors and open small targeted PRs.",
    },
  },
  {
    label: "PR babysitter",
    description: "Watches open PRs, nudges stale ones, addresses CodeRabbit feedback automatically.",
    preset: {
      name: "pr-babysitter",
      description: "Keeps PRs moving, nudges stale ones, addresses CodeRabbit comments.",
      skills: "dev-autoship-code, api-github-actions",
    },
  },
  {
    label: "Lint fixer",
    description: "Runs lint across the repo on a schedule and ships fix PRs for trivial violations.",
    preset: {
      name: "lint-fixer",
      description: "Auto-fixes trivial lint violations across the repo.",
      skills: "api-eslint, dev-autoship-code",
      setup_commands: "pnpm install",
    },
  },
]

const BLANK_FORM: CreateForm = {
  name: "",
  repo_path: "",
  base_branch: "main",
  runner: "claude-code",
  model: "",
  description: "",
  system_prompt: "",
  skills: "",
  setup_commands: "",
  slack_channel_id: "",
}

const STATUS_LABEL: Record<AgentHealth, string> = {
  healthy: "Healthy",
  running: "Running",
  failing: "Failing",
  stale: "Stale",
  disabled: "Disabled",
  "no-recent-runs": "No recent runs",
}

const STATUS_BADGE_CLASS: Record<AgentHealth, string> = {
  healthy: "border-primary/30 bg-primary/10 text-primary",
  running: "border-primary/30 bg-primary/10 text-primary",
  failing: "border-destructive/30 bg-destructive/10 text-destructive",
  stale: "border-border bg-secondary text-foreground",
  disabled: "border-border bg-muted text-muted-foreground",
  "no-recent-runs": "border-border bg-background text-muted-foreground",
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All status" },
  { value: "healthy", label: "Healthy" },
  { value: "running", label: "Running" },
  { value: "failing", label: "Failing" },
  { value: "stale", label: "Stale" },
  { value: "disabled", label: "Disabled" },
  { value: "no-recent-runs", label: "No recent runs" },
]

const CADENCE_OPTIONS: { value: CadenceFilter; label: string }[] = [
  { value: "all", label: "All cadence" },
  { value: "scheduled", label: "Scheduled" },
  { value: "message", label: "Message" },
  { value: "manual", label: "Manual" },
  { value: "disabled", label: "Disabled" },
]

function fmtDuration(start?: string | null, end?: string | null): string {
  if (!start) return ""
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() : Date.now()
  const sec = Math.max(0, Math.round((e - s) / 1000))
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m ${sec % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

function fmtAge(iso?: string | null): string {
  if (!iso) return "never"
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (sec < 60) return `${sec}s ago`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function ageInDays(iso?: string | null): number | null {
  if (!iso) return null
  return Math.max(0, (Date.now() - new Date(iso).getTime()) / 86_400_000)
}

function describeCron(expr: string): string | null {
  const trimmed = expr.trim()
  if (!trimmed) return null
  const parts = trimmed.split(/\s+/)
  if (parts.length < 5 || parts.length > 6) return null
  const [min, hour, dom, mon, dow] = parts
  if (min === "*" && hour === "*" && dom === "*" && mon === "*" && dow === "*") return "Every minute"
  const isNum = (value: string) => /^\d+$/.test(value)
  if (isNum(min) && isNum(hour) && dom === "*" && mon === "*" && dow === "*") {
    return `Daily at ${hour.padStart(2, "0")}:${min.padStart(2, "0")}`
  }
  if (isNum(min) && hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    return `Hourly at :${min.padStart(2, "0")}`
  }
  if (min.startsWith("*/") && hour === "*") return `Every ${min.slice(2)} minutes`
  if (isNum(min) && isNum(hour) && dom === "*" && mon === "*" && /^[0-6]$/.test(dow)) {
    const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][Number(dow)]
    return `${day} at ${hour.padStart(2, "0")}:${min.padStart(2, "0")}`
  }
  return null
}

function toArr(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function agentOwner(agent: Agent): string {
  return agent.created_by || "unassigned"
}

function repoName(path: string): string {
  return path.split("/").filter(Boolean).pop() || path
}

function getLatestRun(agentId: number, runs: Run[]): Run | undefined {
  return runs
    .filter((run) => run.agent_id === agentId)
    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime())[0]
}

function getAgentHealth(agent: Agent, latestRun?: Run): AgentHealth {
  if (!agent.enabled) return "disabled"
  if (!latestRun) return "no-recent-runs"
  if (RUNNING_STATUSES.has(latestRun.status)) return "running"
  if (latestRun.status === "failed") return "failing"
  const age = ageInDays(latestRun.started_at)
  if (age != null && age >= NO_RECENT_RUN_DAYS) return "no-recent-runs"
  if (age != null && age >= STALE_RUN_DAYS) return "stale"
  return "healthy"
}

function getCadenceFromTriggers(triggers?: Trigger[]): CadenceFilter {
  const enabledTriggers = (triggers ?? []).filter((trigger) => trigger.enabled)
  if (enabledTriggers.some((trigger) => trigger.type === "cron")) return "scheduled"
  if (enabledTriggers.some((trigger) => trigger.type === "message")) return "message"
  if (enabledTriggers.some((trigger) => trigger.type === "manual")) return "manual"
  return "disabled"
}

function getNextSchedule(triggers: Trigger[]): string {
  const cron = triggers.find((trigger) => trigger.enabled && trigger.type === "cron" && trigger.cron_expr)
  if (cron?.cron_expr) return describeCron(cron.cron_expr) ?? cron.cron_expr
  const message = triggers.find((trigger) => trigger.enabled && trigger.type === "message")
  if (message?.message_pattern) return `Message: /${message.message_pattern}/`
  const manual = triggers.find((trigger) => trigger.enabled && trigger.type === "manual")
  return manual ? "Manual trigger available" : "No enabled schedule"
}

function formatCadence(cadence: CadenceFilter): string {
  return CADENCE_OPTIONS.find((option) => option.value === cadence)?.label ?? cadence
}

function coerceTab(tab: DetailTab | undefined): DetailTab {
  return tab ?? "overview"
}

function coerceNumberParam(value: number | string | undefined): number | null {
  if (value == null) return null
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function coerceSelectedId(agent: number | string | undefined): number | null {
  return coerceNumberParam(agent)
}

function filterValue<T extends string>(value: T | undefined, fallback: T): T {
  return value ?? fallback
}

export default function AgentsScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as AgentsSearch
  const selectedId = coerceSelectedId(search.agent)
  const selectedRunId = coerceNumberParam(search.run)
  const creating = search.new === "1"
  const searchRef = useRef<HTMLInputElement>(null)

  const setSearch = (next: Partial<AgentsSearch>) =>
    navigate({
      to: "/agents",
      search: (prev: Record<string, unknown>) => {
        const merged = { ...(prev as AgentsSearch), ...next }
        ;(Object.keys(merged) as (keyof AgentsSearch)[]).forEach((key) => {
          if (merged[key] === undefined || merged[key] === "" || merged[key] === "all") delete merged[key]
        })
        return merged
      },
    })

  const listQuery = trpc.brainAgents.list.useQuery()
  const jobsQuery = trpc.brainAgents.jobs.useQuery(
    { limit: 500 },
    {
      refetchInterval: (data: { runs?: Run[] } | undefined) => {
        const hasLive = (data?.runs ?? []).some((run) => RUNNING_STATUSES.has(run.status))
        return hasLive ? 5000 : 30000
      },
    },
  )
  const { data: runnersData } = trpc.brainAgents.runners.useQuery()
  const runnersResponse = runnersData as { runners?: string[] } | undefined
  const runners: string[] = runnersResponse?.runners ?? ["claude-code", "codex"]
  const utils = trpc.useUtils()

  const createMutation = trpc.brainAgents.create.useMutation({
    onSuccess: (res: { agent?: Agent }) => {
      toast.success("Agent created")
      void listQuery.refetch()
      if (res?.agent?.id) setSearch({ agent: res.agent.id, new: undefined, tab: "config", template: undefined })
    },
    onError: (err) => toast.error(err.message || "Failed to create agent"),
  })

  const agentsResponse = listQuery.data as { agents?: Agent[] } | undefined
  const jobsResponse = jobsQuery.data
  const agents = useMemo(() => agentsResponse?.agents ?? [], [agentsResponse?.agents])
  const runs = useMemo(() => jobsResponse?.runs ?? [], [jobsResponse?.runs])
  const rows = useMemo<AgentOpsRow[]>(() => {
    return agents.map((agent) => {
      const latestRun = getLatestRun(agent.id, runs)
      return {
        agent,
        latestRun,
        health: getAgentHealth(agent, latestRun),
        cadence: getCadenceFromTriggers(agent.triggers),
        owner: agentOwner(agent),
      }
    })
  }, [agents, runs])

  const owners = useMemo(() => {
    const values = Array.from(new Set(rows.map((row) => row.owner))).sort((a, b) => a.localeCompare(b))
    return values.length > 0 ? values : ["unassigned"]
  }, [rows])

  const filters = {
    status: filterValue<StatusFilter>(search.status, "all"),
    runner: filterValue(search.runner, "all"),
    cadence: filterValue<CadenceFilter>(search.cadence, "all"),
    owner: filterValue(search.owner, "all"),
    q: search.q ?? "",
  }

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase()
    return rows.filter((row) => {
      const agent = row.agent
      const matchesSearch =
        !q ||
        agent.name.toLowerCase().includes(q) ||
        agent.repo_path.toLowerCase().includes(q) ||
        agent.runner.toLowerCase().includes(q) ||
        agentOwner(agent).toLowerCase().includes(q) ||
        (agent.description ?? "").toLowerCase().includes(q)
      const matchesStatus = filters.status === "all" || row.health === filters.status
      const matchesRunner = filters.runner === "all" || agent.runner === filters.runner
      const matchesCadence = filters.cadence === "all" || row.cadence === filters.cadence
      const matchesOwner = filters.owner === "all" || row.owner === filters.owner
      return matchesSearch && matchesStatus && matchesRunner && matchesCadence && matchesOwner
    })
  }, [filters.cadence, filters.owner, filters.q, filters.runner, filters.status, rows])

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        if (row.health === "running") acc.running += 1
        if (row.health === "failing") acc.failing += 1
        if (row.health === "stale") acc.stale += 1
        if (row.health === "disabled") acc.disabled += 1
        if (row.health === "no-recent-runs") acc.noRecentRuns += 1
        return acc
      },
      { running: 0, failing: 0, stale: 0, disabled: 0, noRecentRuns: 0 },
    )
  }, [rows])

  useHotkeys("n", (event) => {
    event.preventDefault()
    setSearch({ new: "1", agent: undefined })
  })
  useHotkeys("/", (event) => {
    event.preventDefault()
    searchRef.current?.focus()
  })
  useHotkeys("esc", () => {
    if (creating) setSearch({ new: undefined, template: undefined })
  })

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="border-b border-border bg-background px-4 py-3 md:px-5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <h1 data-testid="agents-heading" className="text-xl font-semibold text-foreground">
                Agent Ops
              </h1>
              <Badge variant="outline" className="hidden md:inline-flex">
                {filtered.length}/{agents.length} visible
              </Badge>
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground">
              Brain-server workers, schedules, ownership, and live run health.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="agents-all-runs-btn"
              variant="outline"
              onClick={() => navigate({ to: "/agents/runs" })}
            >
              <History className="mr-2 h-4 w-4" />
              All runs
            </Button>
            <Button data-testid="agents-new-btn" onClick={() => setSearch({ new: "1", agent: undefined })}>
              <Plus className="mr-2 h-4 w-4" />
              New agent
            </Button>
          </div>
        </div>
      </header>

      <HealthSummary summary={summary} total={agents.length} />

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[440px] shrink-0 flex-col border-r border-border bg-background">
          <AgentFilters
            searchRef={searchRef}
            filters={filters}
            runners={runners}
            owners={owners}
            onChange={setSearch}
          />

          <div className="min-h-0 flex-1 overflow-y-auto">
            {listQuery.isLoading && <LoadingBlock label="Loading agents" />}

            {!listQuery.isLoading && filtered.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground">
                {agents.length === 0 ? "No agents yet." : "No agents match these filters."}
              </div>
            )}

            <div className="divide-y divide-border">
              {filtered.map((row) => (
                <AgentListRow
                  key={row.agent.id}
                  row={row}
                  selected={selectedId === row.agent.id}
                  onClick={() => setSearch({ agent: row.agent.id, new: undefined, tab: coerceTab(search.tab) })}
                />
              ))}
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 overflow-y-auto">
          {creating ? (
            <div className="p-4 md:p-6">
              <CreateAgentForm
                runners={runners}
                templateRaw={search.template}
                onCancel={() => setSearch({ new: undefined, template: undefined })}
                onCreate={(form) => createMutation.mutate(form)}
                pending={createMutation.isLoading}
                error={createMutation.error?.message}
              />
            </div>
          ) : selectedId != null ? (
            <AgentDetail
              key={selectedId}
              agentId={selectedId}
              tab={coerceTab(search.tab)}
              onTab={(tab) => setSearch({ tab })}
              onOpenRun={(runId) => setSearch({ run: runId })}
              runners={runners}
              allRuns={runs}
              onDeleted={() => {
                setSearch({ agent: undefined, tab: undefined })
                void listQuery.refetch()
              }}
              onChanged={() => {
                void listQuery.refetch()
                void utils.brainAgents.get.invalidate({ id: selectedId })
              }}
            />
          ) : (
            <EmptyState
              onPickTemplate={(preset) => setSearch({ new: "1", template: encodeURIComponent(JSON.stringify(preset)) })}
            />
          )}
        </main>
      </div>

      {selectedRunId != null && <RunLogDrawer runId={selectedRunId} onClose={() => setSearch({ run: undefined })} />}
    </div>
  )
}

function HealthSummary({
  summary,
  total,
}: {
  summary: { running: number; failing: number; stale: number; disabled: number; noRecentRuns: number }
  total: number
}) {
  return (
    <section className="grid grid-cols-2 border-b border-border bg-secondary/40 md:grid-cols-6">
      <HealthCell icon={<CircleDot className="h-4 w-4" />} label="Total" value={total} />
      <HealthCell icon={<Activity className="h-4 w-4" />} label="Running" value={summary.running} />
      <HealthCell
        icon={<AlertTriangle className="h-4 w-4" />}
        label="Failing"
        value={summary.failing}
        alert={summary.failing > 0}
      />
      <HealthCell icon={<Clock3 className="h-4 w-4" />} label="Stale" value={summary.stale} />
      <HealthCell icon={<PauseCircle className="h-4 w-4" />} label="Disabled" value={summary.disabled} />
      <HealthCell icon={<History className="h-4 w-4" />} label="No recent runs" value={summary.noRecentRuns} />
    </section>
  )
}

function HealthCell({
  icon,
  label,
  value,
  alert = false,
}: {
  icon: React.ReactNode
  label: string
  value: number
  alert?: boolean
}) {
  return (
    <div className="flex min-h-16 items-center gap-3 border-r border-border px-4 py-3 last:border-r-0">
      <div className={alert ? "text-destructive" : "text-muted-foreground"}>{icon}</div>
      <div>
        <div className={alert ? "text-xl font-semibold text-destructive" : "text-xl font-semibold text-foreground"}>
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  )
}

function AgentFilters({
  searchRef,
  filters,
  runners,
  owners,
  onChange,
}: {
  searchRef: React.RefObject<HTMLInputElement>
  filters: { status: StatusFilter; runner: string; cadence: CadenceFilter; owner: string; q: string }
  runners: string[]
  owners: string[]
  onChange: (next: Partial<AgentsSearch>) => void
}) {
  return (
    <div className="space-y-2 border-b border-border p-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Filters
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          ref={searchRef}
          data-testid="agents-search-input"
          type="text"
          value={filters.q}
          onChange={(event) => onChange({ q: event.target.value || undefined })}
          placeholder="Search agents, repos, owners"
          className="pl-8"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FilterSelect
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(value) => onChange({ status: value as StatusFilter })}
        />
        <FilterSelect
          value={filters.cadence}
          options={CADENCE_OPTIONS}
          onChange={(value) => onChange({ cadence: value as CadenceFilter })}
        />
        <FilterSelect
          value={filters.runner}
          options={[
            { value: "all", label: "All runners" },
            ...runners.map((runner) => ({ value: runner, label: runner })),
          ]}
          onChange={(value) => onChange({ runner: value })}
        />
        <FilterSelect
          value={filters.owner}
          options={[{ value: "all", label: "All owners" }, ...owners.map((owner) => ({ value: owner, label: owner }))]}
          onChange={(value) => onChange({ owner: value })}
        />
      </div>
    </div>
  )
}

function FilterSelect({
  value,
  options,
  onChange,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className="h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function AgentListRow({ row, selected, onClick }: { row: AgentOpsRow; selected: boolean; onClick: () => void }) {
  const { agent, latestRun, health } = row

  return (
    <button
      data-testid="agents-list-row"
      onClick={onClick}
      className={`grid w-full grid-cols-[minmax(0,1fr)_104px_88px] items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary ${
        selected ? "bg-secondary" : ""
      }`}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-semibold text-foreground">{agent.name}</span>
          {!agent.enabled && <PauseCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {repoName(agent.repo_path)} / {agent.base_branch}
        </div>
      </div>
      <div className="min-w-0">
        <Badge
          variant="outline"
          className={`max-w-full justify-center truncate rounded-sm ${STATUS_BADGE_CLASS[health]}`}
        >
          {STATUS_LABEL[health]}
        </Badge>
      </div>
      <div className="min-w-0 text-right text-xs text-muted-foreground">
        <div className="truncate">{agent.runner}</div>
        <div className="truncate">{latestRun ? fmtAge(latestRun.started_at) : "never"}</div>
      </div>
    </button>
  )
}

function EmptyState({ onPickTemplate }: { onPickTemplate: (preset: Partial<CreateForm>) => void }) {
  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-5 flex items-start gap-3">
        <ShieldCheck className="mt-1 h-5 w-5 text-primary" />
        <div>
          <h2 className="text-lg font-semibold text-foreground">Select an agent or start from a template</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Templates pre-fill the create flow. You can edit every field before saving.
          </p>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {STARTER_TEMPLATES.map((template) => (
          <button
            key={template.label}
            onClick={() => onPickTemplate(template.preset)}
            className="min-h-32 rounded-md border border-border bg-background p-4 text-left transition-colors hover:bg-secondary"
          >
            <div className="font-semibold text-foreground">{template.label}</div>
            <div className="mt-2 text-sm text-muted-foreground">{template.description}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

function CreateAgentForm({
  runners,
  templateRaw,
  onCancel,
  onCreate,
  pending,
  error,
}: {
  runners: string[]
  templateRaw?: string
  onCancel: () => void
  onCreate: (form: {
    name: string
    repo_path: string
    base_branch: string
    runner: string
    model?: string
    description?: string
    system_prompt?: string
    skills: string[]
    setup_commands: string[]
    slack_channel_id?: string
  }) => void
  pending: boolean
  error?: string
}) {
  const initial = useMemo<CreateForm>(() => {
    if (templateRaw) {
      try {
        const preset = JSON.parse(decodeURIComponent(templateRaw)) as Partial<CreateForm>
        return { ...BLANK_FORM, ...preset }
      } catch {
        return { ...BLANK_FORM }
      }
    }
    return { ...BLANK_FORM }
  }, [templateRaw])

  const [form, setForm] = useState<CreateForm>(initial)
  const set =
    (key: keyof CreateForm) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: event.target.value })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    onCreate({
      name: form.name,
      repo_path: form.repo_path,
      base_branch: form.base_branch || "main",
      runner: form.runner,
      model: form.model || undefined,
      description: form.description || undefined,
      system_prompt: form.system_prompt || undefined,
      skills: toArr(form.skills),
      setup_commands: toArr(form.setup_commands),
      slack_channel_id: form.slack_channel_id || undefined,
    })
  }

  return (
    <form data-testid="agents-create-form" onSubmit={submit} className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">New agent</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Create a brain-server worker with repo, runner, prompt, and optional Slack routing.
          </p>
        </div>
        <Button data-testid="agents-create-cancel-btn" type="button" variant="outline" onClick={onCancel}>
          <X className="mr-2 h-4 w-4" />
          Cancel
        </Button>
      </div>
      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm text-destructive">
          {error}
        </div>
      )}
      <Field label="Name *">
        <Input
          data-testid="agents-create-name-input"
          required
          value={form.name}
          onChange={set("name")}
          placeholder="dep-cleanup"
        />
      </Field>
      <Field label="Repo path *">
        <Input required value={form.repo_path} onChange={set("repo_path")} placeholder="/Users/.../aix-turborepo-aix" />
      </Field>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Base branch">
          <Input value={form.base_branch} onChange={set("base_branch")} placeholder="develop" />
        </Field>
        <Field label="Runner">
          <select value={form.runner} onChange={set("runner")} className={selectCls}>
            {runners.map((runner) => (
              <option key={runner} value={runner}>
                {runner}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Model">
          <Input value={form.model} onChange={set("model")} placeholder="opus / gpt-5.5" />
        </Field>
        <Field label="Slack channel id">
          <Input value={form.slack_channel_id} onChange={set("slack_channel_id")} placeholder="C0123..." />
        </Field>
      </div>
      <Field label="Description">
        <Input value={form.description} onChange={set("description")} />
      </Field>
      <Field label="System prompt">
        <textarea value={form.system_prompt} onChange={set("system_prompt")} className={textareaCls} rows={3} />
      </Field>
      <Field label="Skills">
        <textarea
          value={form.skills}
          onChange={set("skills")}
          className={textareaCls}
          rows={2}
          placeholder="dev-autoship-code, api-websearch"
        />
      </Field>
      <Field label="Setup commands">
        <textarea
          value={form.setup_commands}
          onChange={set("setup_commands")}
          className={textareaCls}
          rows={2}
          placeholder="bun install"
        />
      </Field>
      <Button data-testid="agents-create-submit-btn" type="submit" loading={pending}>
        <Plus className="mr-2 h-4 w-4" />
        Create agent
      </Button>
    </form>
  )
}

function AgentDetail({
  agentId,
  tab,
  onTab,
  onOpenRun,
  runners,
  allRuns,
  onDeleted,
  onChanged,
}: {
  agentId: number
  tab: DetailTab
  onTab: (tab: DetailTab) => void
  onOpenRun: (runId: number) => void
  runners: string[]
  allRuns: Run[]
  onDeleted: () => void
  onChanged: () => void
}) {
  const detailQuery = trpc.brainAgents.get.useQuery(
    { id: agentId },
    {
      refetchInterval: (data: AgentDetailData | undefined) => {
        const hasLive = (data?.recent_runs ?? []).some((run) => RUNNING_STATUSES.has(run.status))
        return hasLive ? 2000 : false
      },
    },
  )
  const cronHealthQuery = trpc.brainAgents.cronHealth.useQuery(undefined, { refetchInterval: 30_000 })

  const update = trpc.brainAgents.update.useMutation({
    onSuccess: () => {
      toast.success("Saved")
      void detailQuery.refetch()
      onChanged()
    },
    onError: (err) => toast.error(err.message || "Save failed"),
  })
  const remove = trpc.brainAgents.remove.useMutation({
    onSuccess: () => {
      toast.success("Agent deleted")
      onDeleted()
    },
    onError: (err) => toast.error(err.message || "Delete failed"),
  })
  const run = trpc.brainAgents.run.useMutation({
    onSuccess: () => {
      toast.success("Run queued")
      void detailQuery.refetch()
    },
    onError: (err) => toast.error(err.message || "Run failed"),
  })
  const addTrigger = trpc.brainAgents.addTrigger.useMutation({
    onSuccess: () => {
      toast.success("Trigger added")
      void detailQuery.refetch()
    },
    onError: (err) => toast.error(err.message || "Add trigger failed"),
  })
  const removeTrigger = trpc.brainAgents.removeTrigger.useMutation({
    onSuccess: (result) => {
      const fallback = result as { disabled?: boolean; fallback_reason?: string }
      toast.success(fallback.disabled ? "Trigger delete failed; disabled instead" : "Trigger removed")
      void detailQuery.refetch()
    },
    onError: (err) => toast.error(err.message || "Remove failed"),
  })
  const toggleTrigger = trpc.brainAgents.toggleTrigger.useMutation({
    onSuccess: () => {
      void detailQuery.refetch()
    },
    onError: (err) => toast.error(err.message || "Toggle failed"),
  })

  if (detailQuery.isLoading || !detailQuery.data) return <LoadingBlock label="Loading agent" />

  const data = detailQuery.data
  const agent = data.agent
  const triggers = data.triggers ?? []
  const cronTrustRows = ((cronHealthQuery.data as { rows?: CronTrustRow[] } | undefined)?.rows ?? []).filter(
    (row) => row.agent?.id === agent.id,
  )
  const detailRuns = data.recent_runs ?? []
  const latestRun = detailRuns[0] ?? getLatestRun(agent.id, allRuns)
  const health = getAgentHealth(agent, latestRun)
  const cadence = getCadenceFromTriggers(triggers)
  const nextSchedule = getNextSchedule(triggers)

  return (
    <div className="min-h-full bg-background">
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 px-4 py-4 backdrop-blur md:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-2xl font-semibold text-foreground">{agent.name}</h2>
              <Badge variant="outline" className={`rounded-sm ${STATUS_BADGE_CLASS[health]}`}>
                {STATUS_LABEL[health]}
              </Badge>
              <Badge variant="secondary" className="rounded-sm">
                {agent.runner}
                {agent.model ? ` / ${agent.model}` : ""}
              </Badge>
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground">
              {agent.repo_path} / {agent.base_branch}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" loading={run.isLoading} onClick={() => run.mutate({ id: agent.id })}>
              <Play className="mr-2 h-4 w-4" />
              Run
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => update.mutate({ id: agent.id, enabled: !agent.enabled })}
            >
              <PauseCircle className="mr-2 h-4 w-4" />
              {agent.enabled ? "Pause" : "Enable"}
            </Button>
            <ButtonWithConfirm
              size="sm"
              variant="destructive"
              tooltipContent={(confirmed) => (confirmed ? "Click again to confirm" : "Delete this agent")}
              onClick={() => remove.mutate({ id: agent.id })}
              render={(confirmed) => <span>{confirmed ? "Confirm?" : "Delete"}</span>}
            />
          </div>
        </div>

        <AgentDetailStats
          agent={agent}
          latestRun={latestRun}
          cadence={cadence}
          health={health}
          nextSchedule={nextSchedule}
        />

        <Tabs value={tab} onValueChange={(value) => onTab(value as DetailTab)} className="mt-4">
          <TabsList size="sm" className="flex h-9 w-fit max-w-full overflow-x-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
            <TabsTrigger value="triggers">Triggers ({triggers.length})</TabsTrigger>
            <TabsTrigger value="cron-trust">Cron Trust</TabsTrigger>
            <TabsTrigger value="runs">Runs ({detailRuns.length})</TabsTrigger>
            <TabsTrigger value="ownership">Ownership</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="p-4 md:p-6">
        <Tabs value={tab} onValueChange={(value) => onTab(value as DetailTab)}>
          <TabsContent value="overview" className="mt-0">
            <OverviewTab
              agent={agent}
              triggers={triggers}
              runs={detailRuns}
              health={health}
              onOpenRun={onOpenRun}
              onRun={() => run.mutate({ id: agent.id })}
              running={run.isLoading}
            />
          </TabsContent>

          <TabsContent value="chat" className="mt-0">
            <AgentChatPanel initialAgentId={agent.id} onOpenRun={onOpenRun} />
          </TabsContent>

          <TabsContent value="config" className="mt-0">
            <ConfigEditor
              agent={agent}
              runners={runners}
              onSave={(patch) => update.mutate({ id: agent.id, ...patch })}
              pending={update.isLoading}
            />
          </TabsContent>

          <TabsContent value="triggers" className="mt-0">
            <TriggersTab
              agent={agent}
              triggers={triggers}
              onAdd={(trigger) => addTrigger.mutate(trigger)}
              onRemove={(triggerId) => removeTrigger.mutate({ agentId: agent.id, triggerId })}
              onToggle={(triggerId) => toggleTrigger.mutate({ agentId: agent.id, triggerId })}
              addError={addTrigger.error?.message}
            />
          </TabsContent>

          <TabsContent value="cron-trust" className="mt-0">
            <CronTrustTab
              rows={cronTrustRows}
              loading={cronHealthQuery.isLoading}
              error={cronHealthQuery.error?.message}
              onOpenRun={onOpenRun}
            />
          </TabsContent>

          <TabsContent value="runs" className="mt-0">
            <RunsTab
              runs={detailRuns}
              onRunPrompt={(prompt) => run.mutate({ id: agent.id, prompt })}
              running={run.isLoading}
              onOpenRun={onOpenRun}
            />
          </TabsContent>

          <TabsContent value="ownership" className="mt-0">
            <OwnershipEditor
              value={{
                owner: agentOwner(agent),
                cadence: nextSchedule,
                escalation: agent.slack_channel_id ?? "",
                notes: agent.description ?? "",
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

function AgentDetailStats({
  agent,
  latestRun,
  cadence,
  health,
  nextSchedule,
}: {
  agent: Agent
  latestRun?: Run
  cadence: CadenceFilter
  health: AgentHealth
  nextSchedule: string
}) {
  return (
    <div className="mt-4 grid gap-2 md:grid-cols-5">
      <MiniStat icon={<UserRound className="h-4 w-4" />} label="Owner" value={agentOwner(agent)} />
      <MiniStat icon={<CalendarClock className="h-4 w-4" />} label="Cadence" value={formatCadence(cadence)} />
      <MiniStat icon={<CheckCircle2 className="h-4 w-4" />} label="Status" value={STATUS_LABEL[health]} />
      <MiniStat
        icon={<History className="h-4 w-4" />}
        label="Latest run"
        value={latestRun ? `${latestRun.status} / ${fmtAge(latestRun.started_at)}` : "Never"}
      />
      <MiniStat icon={<Bell className="h-4 w-4" />} label="Next schedule" value={nextSchedule} />
    </div>
  )
}

function MiniStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-md border border-border bg-secondary/40 px-3 py-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 truncate text-sm font-medium text-foreground" title={value}>
        {value}
      </div>
    </div>
  )
}

function OverviewTab({
  agent,
  triggers,
  runs,
  health,
  onOpenRun,
  onRun,
  running,
}: {
  agent: Agent
  triggers: Trigger[]
  runs: Run[]
  health: AgentHealth
  onOpenRun: (runId: number) => void
  onRun: () => void
  running: boolean
}) {
  const enabledTriggers = triggers.filter((trigger) => trigger.enabled)

  return (
    <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
      <section className="space-y-4">
        <Panel title="Operational posture" icon={<Activity className="h-4 w-4" />}>
          <div className="grid gap-3 md:grid-cols-3">
            <Metric label="Health" value={STATUS_LABEL[health]} />
            <Metric label="Enabled triggers" value={String(enabledTriggers.length)} />
            <Metric label="Recent runs" value={String(runs.length)} />
          </div>
          <div className="mt-4 text-sm text-muted-foreground">{agent.description || "No description set."}</div>
        </Panel>

        <Panel title="Recent runs" icon={<History className="h-4 w-4" />}>
          <RunRows runs={runs.slice(0, 5)} onOpenRun={onOpenRun} />
          <Button className="mt-3" size="sm" loading={running} onClick={onRun}>
            <Play className="mr-2 h-4 w-4" />
            Run now
          </Button>
        </Panel>
      </section>

      <section className="space-y-4">
        <Panel title="Triggers" icon={<CalendarClock className="h-4 w-4" />}>
          {triggers.length === 0 ? (
            <div className="text-sm text-muted-foreground">No triggers configured.</div>
          ) : (
            <div className="space-y-2">
              {triggers.slice(0, 4).map((trigger) => (
                <TriggerSummary key={trigger.id} trigger={trigger} />
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Execution config" icon={<Settings2 className="h-4 w-4" />}>
          <div className="space-y-2 text-sm">
            <KeyValue label="Runner" value={agent.runner} />
            <KeyValue label="Model" value={agent.model || "Default"} />
            <KeyValue label="Branch" value={agent.base_branch} />
            <KeyValue label="Skills" value={agent.skills.length > 0 ? agent.skills.join(", ") : "None"} />
          </div>
        </Panel>
      </section>
    </div>
  )
}

function ConfigEditor({
  agent,
  runners,
  onSave,
  pending,
}: {
  agent: Agent
  runners: string[]
  onSave: (patch: {
    runner: string
    model: string | null
    base_branch: string
    description: string | null
    system_prompt: string | null
    skills: string[]
    setup_commands: string[]
    slack_channel_id: string | null
  }) => void
  pending: boolean
}) {
  const [state, setState] = useState({
    runner: agent.runner,
    model: agent.model || "",
    base_branch: agent.base_branch,
    description: agent.description || "",
    system_prompt: agent.system_prompt || "",
    skills: agent.skills.join(", "),
    setup_commands: agent.setup_commands.join(", "),
    slack_channel_id: agent.slack_channel_id || "",
  })

  return (
    <div className="max-w-3xl space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Runner">
          <select
            value={state.runner}
            onChange={(event) => setState({ ...state, runner: event.target.value })}
            className={selectCls}
          >
            {runners.map((runner) => (
              <option key={runner} value={runner}>
                {runner}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Model">
          <Input value={state.model} onChange={(event) => setState({ ...state, model: event.target.value })} />
        </Field>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Base branch">
          <Input
            value={state.base_branch}
            onChange={(event) => setState({ ...state, base_branch: event.target.value })}
          />
        </Field>
        <Field label="Slack channel id">
          <Input
            value={state.slack_channel_id}
            onChange={(event) => setState({ ...state, slack_channel_id: event.target.value })}
          />
        </Field>
      </div>
      <Field label="Description">
        <Input
          value={state.description}
          onChange={(event) => setState({ ...state, description: event.target.value })}
        />
      </Field>
      <Field label="System prompt">
        <textarea
          value={state.system_prompt}
          onChange={(event) => setState({ ...state, system_prompt: event.target.value })}
          className={textareaCls}
          rows={4}
        />
      </Field>
      <Field label="Skills">
        <textarea
          value={state.skills}
          onChange={(event) => setState({ ...state, skills: event.target.value })}
          className={textareaCls}
          rows={2}
        />
      </Field>
      <Field label="Setup commands">
        <textarea
          value={state.setup_commands}
          onChange={(event) => setState({ ...state, setup_commands: event.target.value })}
          className={textareaCls}
          rows={2}
        />
      </Field>
      <Button
        loading={pending}
        onClick={() =>
          onSave({
            runner: state.runner,
            model: state.model || null,
            base_branch: state.base_branch,
            description: state.description || null,
            system_prompt: state.system_prompt || null,
            skills: toArr(state.skills),
            setup_commands: toArr(state.setup_commands),
            slack_channel_id: state.slack_channel_id || null,
          })
        }
      >
        Save config
      </Button>
    </div>
  )
}

function TriggersTab({
  agent,
  triggers,
  onAdd,
  onRemove,
  onToggle,
  addError,
}: {
  agent: Agent
  triggers: Trigger[]
  onAdd: (input: {
    agentId: number
    type: "cron" | "message" | "manual"
    prompt: string
    cron_expr?: string
    message_pattern?: string
  }) => void
  onRemove: (triggerId: number) => void
  onToggle: (triggerId: number) => void
  addError?: string
}) {
  const [trigger, setTrigger] = useState<TriggerForm>({ type: "cron", prompt: "", cron_expr: "", message_pattern: "" })
  const cronPreview = trigger.type === "cron" ? describeCron(trigger.cron_expr) : null
  const cronInvalid = trigger.type === "cron" && trigger.cron_expr.trim().length > 0 && cronPreview == null

  return (
    <div className="max-w-4xl space-y-3">
      {triggers.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
          No triggers yet. Add one below to schedule or react to Slack messages.
        </div>
      )}

      {triggers.map((item) => (
        <div key={item.id} className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="rounded-sm uppercase">
                {item.type}
              </Badge>
              {item.cron_expr && <code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{item.cron_expr}</code>}
              {item.cron_expr && describeCron(item.cron_expr) && (
                <span className="text-xs text-muted-foreground">{describeCron(item.cron_expr)}</span>
              )}
              {item.message_pattern && (
                <code className="rounded bg-secondary px-1.5 py-0.5 text-xs">/{item.message_pattern}/</code>
              )}
              {!item.enabled && <Badge variant="outline">disabled</Badge>}
            </div>
            <div className="mt-1 truncate text-sm text-foreground">{item.prompt}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              runs: {item.run_count}
              {item.last_run_at ? ` / last: ${fmtAge(item.last_run_at)}` : ""}
              {item.last_error ? ` / last error: ${item.last_error}` : ""}
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button size="sm" variant="outline" onClick={() => onToggle(item.id)}>
              {item.enabled ? "Off" : "On"}
            </Button>
            <ButtonWithConfirm
              size="sm"
              variant="destructive"
              tooltipContent={(confirmed) => (confirmed ? "Confirm remove" : "Remove trigger")}
              onClick={() => onRemove(item.id)}
              render={(confirmed) => <span>{confirmed ? "Confirm" : "Remove"}</span>}
            />
          </div>
        </div>
      ))}

      <div className="rounded-md border border-dashed border-border p-3">
        <div className="grid gap-2 md:grid-cols-[160px_1fr]">
          <select
            value={trigger.type}
            onChange={(event) => setTrigger({ ...trigger, type: event.target.value as TriggerForm["type"] })}
            className={selectCls}
          >
            <option value="cron">cron</option>
            <option value="message">message</option>
            <option value="manual">manual</option>
          </select>
          {trigger.type === "cron" && (
            <div>
              <Input
                value={trigger.cron_expr}
                onChange={(event) => setTrigger({ ...trigger, cron_expr: event.target.value })}
                placeholder="0 9 * * *"
                className={cronInvalid ? "border-destructive" : ""}
              />
              <div className="mt-0.5 h-4 text-xs">
                {cronPreview ? (
                  <span className="text-muted-foreground">{cronPreview}</span>
                ) : cronInvalid ? (
                  <span className="text-destructive">unrecognized cron pattern</span>
                ) : null}
              </div>
            </div>
          )}
          {trigger.type === "message" && (
            <Input
              value={trigger.message_pattern}
              onChange={(event) => setTrigger({ ...trigger, message_pattern: event.target.value })}
              placeholder="deploy.*failed"
            />
          )}
        </div>
        <textarea
          value={trigger.prompt}
          onChange={(event) => setTrigger({ ...trigger, prompt: event.target.value })}
          placeholder="Prompt to run when this fires..."
          className={`${textareaCls} mt-2`}
          rows={2}
        />
        <Button
          className="mt-2"
          onClick={() => {
            onAdd({
              agentId: agent.id,
              type: trigger.type,
              prompt: trigger.prompt,
              cron_expr: trigger.type === "cron" ? trigger.cron_expr : undefined,
              message_pattern: trigger.type === "message" ? trigger.message_pattern : undefined,
            })
            setTrigger({ type: "cron", prompt: "", cron_expr: "", message_pattern: "" })
          }}
          disabled={!trigger.prompt || cronInvalid}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add trigger
        </Button>
        {addError && <div className="mt-2 text-xs text-destructive">{addError}</div>}
      </div>
    </div>
  )
}

function CronTrustTab({
  rows,
  loading,
  error,
  onOpenRun,
}: {
  rows: CronTrustRow[]
  loading: boolean
  error?: string
  onOpenRun: (runId: number) => void
}) {
  if (loading) return <LoadingBlock label="Loading cron trust" />
  if (error) return <div className="rounded-md border border-destructive p-3 text-sm text-destructive">{error}</div>
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
        No cron triggers for this agent. Message and manual triggers do not use the cron ledger.
      </div>
    )
  }

  return (
    <div className="max-w-5xl space-y-3">
      {rows.map((row) => (
        <div key={row.trigger.id} className="rounded-md border border-border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Badge variant={row.enabled ? "secondary" : "outline"} className="rounded-sm">
                {row.enabled ? "active" : "disabled"}
              </Badge>
              <code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{row.trigger.cron_expr}</code>
              {row.next_due_pst && <span className="text-xs text-muted-foreground">next: {row.next_due_pst}</span>}
            </div>
            <div className="flex shrink-0 gap-2 text-xs text-muted-foreground">
              <span>missed {row.missed_count}</span>
              <span>failed {row.failed_count}</span>
            </div>
          </div>

          <div className="mt-3 grid gap-2 md:grid-cols-3">
            <TrustMetric label="Last scheduled" value={row.latest_fire?.scheduled_for_pst ?? "No ledger row"} />
            <TrustMetric label="Fire status" value={row.latest_fire?.status ?? "none"} />
            <TrustMetric label="Run status" value={row.latest_run?.status ?? "No linked run"} />
          </div>

          {row.latest_fire && (
            <div className="mt-3 space-y-1 text-xs text-muted-foreground">
              <div className="break-all">dedup: {row.latest_fire.dedup_key}</div>
              <div>
                source: {row.latest_fire.source}
                {row.latest_fire.executor_job_id ? ` / job: ${row.latest_fire.executor_job_id}` : ""}
              </div>
              {row.latest_fire.error && <div className="text-destructive">error: {row.latest_fire.error}</div>}
              {row.latest_run?.id && (
                <Button size="sm" variant="outline" className="mt-1" onClick={() => onOpenRun(row.latest_run.id)}>
                  Open run #{row.latest_run.id}
                </Button>
              )}
            </div>
          )}

          {row.recent_fires.length > 0 && (
            <div className="mt-3 space-y-1">
              {row.recent_fires.slice(0, 6).map((fire) => (
                <div
                  key={fire.id}
                  className="grid gap-2 rounded border border-border bg-secondary/40 px-2 py-1 text-xs text-muted-foreground md:grid-cols-[170px_80px_1fr]"
                >
                  <span>{fire.scheduled_for_pst}</span>
                  <span>{fire.status}</span>
                  <span className="truncate">{fire.error || fire.executor_job_id || fire.source}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function TrustMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-secondary/40 p-2">
      <div className="text-[11px] font-semibold uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 break-words text-sm text-foreground">{value}</div>
    </div>
  )
}

function RunsTab({
  runs,
  onRunPrompt,
  running,
  onOpenRun,
}: {
  runs: Run[]
  onRunPrompt: (prompt: string) => void
  running: boolean
  onOpenRun: (runId: number) => void
}) {
  const [prompt, setPrompt] = useState("")
  const wasRunning = useRef(false)
  useEffect(() => {
    if (wasRunning.current && !running) setPrompt("")
    wasRunning.current = running
  }, [running])

  return (
    <div className="max-w-5xl space-y-4">
      <div className="rounded-md border border-border p-3">
        <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Fire a one-off run</div>
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="Investigate the latest failure and open a PR if you find a fix."
          className={textareaCls}
          rows={3}
        />
        <Button className="mt-2" loading={running} disabled={!prompt.trim()} onClick={() => onRunPrompt(prompt.trim())}>
          <Play className="mr-2 h-4 w-4" />
          Run prompt
        </Button>
      </div>

      <RunRows runs={runs} onOpenRun={onOpenRun} />
    </div>
  )
}

function RunRows({ runs, onOpenRun }: { runs: Run[]; onOpenRun: (runId: number) => void }) {
  if (runs.length === 0) return <div className="text-sm text-muted-foreground">No runs yet.</div>

  return (
    <div className="divide-y divide-border rounded-md border border-border">
      {runs.map((run) => (
        <button
          key={run.id}
          onClick={() => onOpenRun(run.id)}
          className="grid w-full grid-cols-[120px_minmax(0,1fr)_160px] items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary"
        >
          <div className="flex items-center gap-2">
            <RunStatusDot status={run.status} />
            <span className="text-xs font-semibold uppercase text-muted-foreground">{run.status}</span>
          </div>
          <span className="truncate text-muted-foreground">{run.prompt || "No prompt"}</span>
          <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
            {run.pr_url && (
              <a
                href={run.pr_url}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="text-foreground underline"
              >
                PR
              </a>
            )}
            <span title={`started ${new Date(run.started_at).toLocaleString()}`}>
              {fmtDuration(run.started_at, run.ended_at)}
              {run.status === "running" ? "..." : ""}
            </span>
            <span>{fmtAge(run.started_at)}</span>
          </div>
        </button>
      ))}
    </div>
  )
}

function RunStatusDot({ status }: { status: Run["status"] }) {
  const className =
    status === "failed"
      ? "bg-destructive"
      : status === "running"
        ? "bg-primary animate-pulse"
        : status === "queued"
          ? "bg-muted-foreground"
          : "bg-primary"
  return <span className={`inline-block h-2 w-2 rounded-full ${className}`} />
}

function OwnershipEditor({ value, onSave }: { value: OwnershipForm; onSave?: (next: OwnershipForm) => void }) {
  const [state, setState] = useState(value)
  const canSave = Boolean(onSave)

  return (
    <div className="max-w-3xl space-y-4">
      <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
        Backend metadata update is not exposed here yet. This component is typed and ready to wire to
        `brainAgents.updateMetadata`.
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Owner">
          <Input value={state.owner} onChange={(event) => setState({ ...state, owner: event.target.value })} />
        </Field>
        <Field label="Cadence">
          <Input value={state.cadence} onChange={(event) => setState({ ...state, cadence: event.target.value })} />
        </Field>
      </div>
      <Field label="Escalation channel">
        <Input
          value={state.escalation}
          onChange={(event) => setState({ ...state, escalation: event.target.value })}
          placeholder="Slack channel or person"
        />
      </Field>
      <Field label="Ownership notes">
        <textarea
          value={state.notes}
          onChange={(event) => setState({ ...state, notes: event.target.value })}
          className={textareaCls}
          rows={4}
        />
      </Field>
      <Button disabled={!canSave} onClick={() => onSave?.(state)}>
        Save ownership
      </Button>
    </div>
  )
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-background p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        {icon}
        {title}
      </div>
      {children}
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-secondary px-3 py-2">
      <div className="text-lg font-semibold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

function TriggerSummary({ trigger }: { trigger: Trigger }) {
  const schedule = trigger.cron_expr
    ? (describeCron(trigger.cron_expr) ?? trigger.cron_expr)
    : trigger.message_pattern
      ? `/${trigger.message_pattern}/`
      : "manual"

  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center justify-between gap-2">
        <Badge variant="secondary" className="rounded-sm uppercase">
          {trigger.type}
        </Badge>
        <span className="text-xs text-muted-foreground">{trigger.enabled ? schedule : "disabled"}</span>
      </div>
      <div className="mt-1 truncate text-sm text-foreground">{trigger.prompt}</div>
    </div>
  )
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-foreground">{value}</span>
    </div>
  )
}

const selectCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
const textareaCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground font-mono"

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="border-border bg-background p-8 text-center text-muted-foreground">
      <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      <div>{label}...</div>
    </div>
  )
}
